import { createContext, useState, useContext, useCallback, useEffect } from "react";
import OnlineOrderHelper from "helpers/OnlineOrderHelper";
import NotificationContext from "./NotificationContext";
import toast from "react-hot-toast";

export const OnlineOrderContext = createContext();

export const OnlineOrderProvider = ({ children }) => {
  const notificationContext = useContext(NotificationContext);
  const [onlineOrders, setOnlineOrders] = useState(null);
  const [loading, setLoading] = useState(false);
  const [processedAlertOrderId, setProcessedAlertOrderId] = useState(null);

  const fetchOnlineOrders = useCallback(
    async (force = false) => {
      // If we already have fetched orders in context and this is not a force refresh, don't refetch
      if (onlineOrders !== null && !force) {
        return onlineOrders;
      }

      try {
        setLoading(true);

        const orders = await OnlineOrderHelper.fetchOnlineOrders();

        // Sort newest first
        orders.sort((a, b) => new Date(b.created_at || b.createdAt) - new Date(a.created_at || a.createdAt));
        setOnlineOrders(orders);

        return orders;
      } catch (err) {
        console.error("Failed to load online orders:", err);
        toast.error(err.message || "Failed to load online orders");

        return [];
      } finally {
        setLoading(false);
      }
    },
    [onlineOrders],
  );

  // Fetch a single order when a new notification arrives and add to context
  const addNewOrderFromNotification = useCallback(async (orderId) => {
    if (!orderId) return;
    try {
      const order = await OnlineOrderHelper.getOnlineOrder(orderId);
      if (order && order.id) {
        setOnlineOrders((prev) => {
          const exists = prev?.some((o) => o.id === order.id);
          if (exists) {
            return prev?.map((o) => (o.id === order.id ? { ...o, ...order } : o));
          }
          return [order, ...(prev || [])];
        });
      }
    } catch (err) {
      console.error(`Failed to fetch incoming online order #${orderId}:`, err);
    }
  }, []);

  // Update a single order in context
  const updateOrderInContext = useCallback((orderId, updates = {}) => {
    const id = Number(orderId);
    if (!id) return;
    setOnlineOrders((prev) =>
      prev?.map((order) => {
        if (order.id === id) {
          return { ...order, ...updates };
        }
        return order;
      }),
    );
  }, []);

  // Update a single online order on server and in context
  const updateOnlineOrder = useCallback(
    async (orderId, updates = {}) => {
      if (!orderId) {
        console.error("updateOnlineOrder: Order ID is required");
        return null;
      }

      updateOrderInContext(orderId, updates);

      // Updating order status in server async
      OnlineOrderHelper.updateOnlineOrder(orderId, updates);
    },
    [updateOrderInContext],
  );

  // Update a single online order status on server and in context
  const updateOnlineOrderStatus = useCallback(
    async (orderId, status) => {
      await updateOnlineOrder(orderId, { status });
    },
    [updateOnlineOrder],
  );

  const getOnlineOrderById = useCallback(
    async (orderId, force = false) => {
      const cachedOrder = onlineOrders?.find((order) => order.id === orderId);
      if (cachedOrder && !force) {
        return cachedOrder;
      }

      // Call api to fetch the order
      const order = await OnlineOrderHelper.getOnlineOrder(orderId);
      if (order && order.id) {
        updateOrderInContext(orderId, order);
        return order;
      }

      return null;
    },
    [onlineOrders, updateOrderInContext],
  );

  // When a new notification arrives in NotificationContext, add that order to context
  useEffect(() => {
    const alertOrderId = notificationContext?.activeAlertOrderId;
    if (alertOrderId && alertOrderId !== processedAlertOrderId) {
      setProcessedAlertOrderId(alertOrderId);
      addNewOrderFromNotification(alertOrderId);
    }
  }, [notificationContext?.activeAlertOrderId, processedAlertOrderId, addNewOrderFromNotification]);

  const value = {
    onlineOrders,
    setOnlineOrders,
    loading,
    fetchOnlineOrders,
    updateOnlineOrder,
    updateOrderInContext,
    addNewOrderFromNotification,
    getOnlineOrderById,
    updateOnlineOrderStatus,
  };

  return <OnlineOrderContext.Provider value={value}>{children}</OnlineOrderContext.Provider>;
};

export default OnlineOrderContext;
